import { useCallback, useMemo } from 'react'

import { useGetBlueprintFamilyDurationDistributionQuery } from '../../../api/blueprintFamilies/durationDistribution'
import { useGetBlueprintFamilyMomentumQuery } from '../../../api/blueprintFamilies/momentum'
import { useGetBlueprintFamilyRollupQuery } from '../../../api/blueprintFamilies/rollup'
import {
  ConventionalScope,
  useGetBlueprintFamiliesQuery,
  useGetBlueprintFamilyGlossaryQuery,
  useGetBlueprintFamilyQuery,
  useGetBlueprintFamilyTrendsQuery,
  useGetBlueprintsQuery,
} from '../../../api/core'
import { isFetchBaseQueryError } from '../../../helpers/isFetchBaseQueryError'
import { CatalogueWindowDays } from '../components/helpers/catalogueUtils'
import { pcConsoleOnlyBlueprints } from '../components/helpers/pcConsoleBlueprints'

export interface UseBlueprintDetailDataOptions {
  windowDays: CatalogueWindowDays
  scope: ConventionalScope
  // The catalogue already handed over a matching Δ snapshot, so the momentum download
  // is not needed.
  skipMomentum?: boolean
}

/**
 * Every request behind the blueprint family page, plus the retry that re-runs the
 * ones that failed.
 *
 * Every call shares one population. No customer-facing endpoint returns discontinued
 * families any more, so a discontinued family opened by direct link or bookmark 404s
 * here rather than rendering — it is no longer reachable from the catalogue either.
 *
 * Each number comes from the endpoint that owns it: the Adoption card's numerator
 * from the list, its denominator from the rollup and its delta from momentum, with
 * the single-family call supplying metadata (names, descriptions) plus a last-resort
 * rate fallback.
 */
export function useBlueprintDetailData(blueprintId: string | undefined, { windowDays, scope, skipMomentum = false }: UseBlueprintDetailDataOptions) {
  const skip = !blueprintId
  const { conventionalSubgenreIds } = scope

  const {
    data: lastBlueprint,
    currentData: currentBlueprint,
    isLoading: isBlueprintLoading,
    isFetching: isBlueprintFetching,
    isError: isBlueprintError,
    error: blueprintError,
    refetch: refetchBlueprint,
  } = useGetBlueprintFamilyQuery({ blueprintFamilyId: blueprintId ?? '', windowDays, conventionalSubgenreIds }, { skip })
  // `data` outlives an args change. A segment or window change keeps the same family's
  // metadata on screen (per-section loading), but after a move to a sibling family it
  // would still be the previous family, so it only stands in when the ids agree.
  const blueprint = currentBlueprint ?? (lastBlueprint?.id === blueprintId ? lastBlueprint : undefined)

  // An unknown or removed family id (e.g. an old bookmark) is a not-found, which no
  // retry can fix. A 404 says so directly. The backend's error responses currently
  // reach the browser without CORS headers, though, so the status is often lost
  // (`FETCH_ERROR`); then the glossary decides. It lists every customer-visible family
  // regardless of window or segment, unlike the catalogue list, which only carries
  // families with events in the window and would misreport quiet families as gone.
  const isBlueprintNotFoundStatus = isFetchBaseQueryError(blueprintError) && blueprintError.status === 404
  const shouldCheckGlossary = isBlueprintError && !isBlueprintNotFoundStatus
  const { data: familyGlossary, isFetching: isFamilyGlossaryFetching } = useGetBlueprintFamilyGlossaryQuery(undefined, {
    skip: skip || !shouldCheckGlossary,
  })
  const isMissingFromGlossary = shouldCheckGlossary && !!familyGlossary?.length && !familyGlossary.some((family) => family.id === blueprintId)
  const isBlueprintNotFound = isBlueprintNotFoundStatus || isMissingFromGlossary
  const isFamilyGlossaryPending = shouldCheckGlossary && !familyGlossary && isFamilyGlossaryFetching

  // Scoped catalogue list: the hierarchy bar's sibling inventory and the adoption
  // tile's numerator. Same args as the catalogue page, so navigating from "Open full
  // details" hits the RTK cache instead of downloading the catalogue again.
  //
  // `data` keeps the previous segment's list while a new one loads (or after it
  // fails); that is fine for the hierarchy menus, which re-filter it by the current
  // segment, but the headline figures read `currentData` so a stale row never
  // passes for the new segment's.
  const {
    data: catalogueList,
    currentData: currentCatalogueList,
    isFetching: isCatalogueListFetching,
    isError: isCatalogueListError,
    refetch: refetchCatalogueList,
  } = useGetBlueprintFamiliesQuery({ windowDays, ...scope }, { skip })

  // The request's cohort — the adoption fraction denominator, read the same way as
  // the catalogue.
  const {
    currentData: groupRollup,
    isFetching: isRollupFetching,
    isError: isRollupError,
    refetch: refetchRollup,
  } = useGetBlueprintFamilyRollupQuery({
    groupBy: 'EVENT_GROUP',
    windowDays,
    ...scope,
  })

  // Net adoption change over the window (momentum) for the Δ adoption tile.
  const {
    currentData: momentumData,
    isFetching: isMomentumFetching,
    isError: isMomentumError,
    refetch: refetchMomentum,
  } = useGetBlueprintFamilyMomentumQuery({ windowDays, ...scope }, { skip: skip || skipMomentum })

  // Monthly adoption + density series for the Density tile and the trend chart.
  const {
    currentData: trends,
    isFetching: isTrendsFetching,
    isError: isTrendsError,
    refetch: refetchTrends,
  } = useGetBlueprintFamilyTrendsQuery({ blueprintFamilyId: blueprintId ?? '', windowDays, conventionalSubgenreIds }, { skip })

  // Game-level blueprints for the drill-down table (whole market; the segment
  // filter is applied client-side inside the table). Keyed by the family alone, so
  // `currentData` is only ever this family's adopters.
  const {
    currentData: gameBlueprints,
    isFetching: isGameBlueprintsFetching,
    isError: isGameBlueprintsError,
    refetch: refetchGameBlueprints,
  } = useGetBlueprintsQuery({ blueprintFamilyId: blueprintId ?? '' }, { skip })
  const isGameBlueprintsLoading = isGameBlueprintsFetching && !gameBlueprints

  // The same list with PC/console adopters added, for the table's PC & Console tab only;
  // everything else on the page stays mobile-only. The tab stays hidden until (and
  // unless) this resolves to at least one game, so its loading and error states are
  // deliberately not surfaced.
  const { currentData: mixedGameBlueprints } = useGetBlueprintsQuery({ blueprintFamilyId: blueprintId ?? '', includePcConsole: true }, { skip })
  const pcConsoleBlueprints = useMemo(() => pcConsoleOnlyBlueprints(gameBlueprints, mixedGameBlueprints), [gameBlueprints, mixedGameBlueprints])

  // Duration-distribution histogram for the "Typical durations" card, scoped to
  // the page's window + resolved segment like the other detail queries.
  const {
    currentData: durationDistribution,
    isFetching: isDurationFetching,
    isError: isDurationError,
    refetch: refetchDuration,
  } = useGetBlueprintFamilyDurationDistributionQuery({ blueprintFamilyId: blueprintId ?? '', windowDays, conventionalSubgenreIds }, { skip })

  // Retry from the error card re-runs every request of the segment that failed, not
  // just the family's, so the page doesn't come back with empty tiles and chart.
  const retryFailedRequests = useCallback(() => {
    refetchBlueprint()
    if (isCatalogueListError) refetchCatalogueList()
    if (isRollupError) refetchRollup()
    if (isMomentumError) refetchMomentum()
    if (isTrendsError) refetchTrends()
    if (isDurationError) refetchDuration()
    if (isGameBlueprintsError) refetchGameBlueprints()
  }, [
    refetchBlueprint,
    isCatalogueListError,
    refetchCatalogueList,
    isRollupError,
    refetchRollup,
    isMomentumError,
    refetchMomentum,
    isTrendsError,
    refetchTrends,
    isDurationError,
    refetchDuration,
    isGameBlueprintsError,
    refetchGameBlueprints,
  ])

  return {
    blueprint,
    currentBlueprint,
    isBlueprintLoading,
    isBlueprintFetching,
    isBlueprintError,
    isBlueprintNotFound,
    isFamilyGlossaryPending,
    catalogueList,
    currentCatalogueList,
    isCatalogueListFetching,
    groupRollup,
    isRollupFetching,
    momentumData,
    isMomentumFetching,
    trends,
    isTrendsFetching,
    isTrendsError,
    refetchTrends,
    gameBlueprints,
    isGameBlueprintsLoading,
    isGameBlueprintsError,
    refetchGameBlueprints,
    pcConsoleBlueprints,
    durationDistribution,
    isDurationFetching,
    retryFailedRequests,
  }
}
